/**
 * Spinoza intent registry.
 * Each intent: { name, description, params, requiresAuth, run(ctx, params) }
 * Write intents require user confirmation via ProductionConfirmDialog.
 */

const INTENTS = [
  {
    name: 'help.explain',
    description: 'Explain a stack element or concept',
    params: [{ name: 'element', type: 'string', required: false }],
    requiresAuth: false,
    keywords: ['explain', 'what is', 'how does', 'help', 'tell me about'],
    run: (ctx, { element }) => {
      const explanations = {
        yield_discovery: 'Yield discovery compares structural frontier yields against your home benchmark, net of fees and FX. You can see how much more you could earn in frontier markets vs. your home government bonds.',
        exchange_rates: 'Exchange rates shows live spot rates and lets you request competitive bank quotes via RFQ. Multiple banks compete to give you the best rate.',
        compliance: 'Compliance fulfilment handles remote KYC, CDS account opening and per-market rule sets. Each bank pre-approves you for their markets.',
        hedging: 'Hedging devices protect your returns from currency fluctuations using pooled forwards, hedge ratios and FX overlay to lock in your exit rate.',
        liquidity: 'Liquidity preservation manages maturity ladders, rollover instructions, netting and a secondary market so you can exit positions early if needed.',
        execution: 'Transaction execution handles auction bids, settlement instructions, CSD confirmation and maintains a full lifecycle audit trail.',
        portfolio: 'Portfolio management consolidates your holdings, performance, risk, FX overlay, governance and projections in one view.',
      };
      return explanations[element] || 'I can explain any of our seven services. Which one would you like to know about?';
    },
  },
  {
    name: 'stack.select',
    description: 'Navigate to a stack element',
    params: [{ name: 'element', type: 'string', required: true }],
    requiresAuth: false,
    keywords: ['show me', 'go to', 'navigate', 'open', 'switch to'],
    run: (ctx, { element }) => {
      if (ctx.selectStack) ctx.selectStack(element);
      return `Switching to ${element.replace(/_/g, ' ')}.`;
    },
  },
  {
    name: 'yield.discover',
    description: 'Run yield discovery',
    params: [],
    requiresAuth: false,
    keywords: ['yields', 'yield discovery', 'compare yields'],
    run: (ctx) => {
      if (ctx.selectStack) ctx.selectStack('yield_discovery');
      return 'Opening yield discovery. Select your source currency and target market to compare yields.';
    },
  },
  {
    name: 'rates.fetch',
    description: 'Fetch exchange rates',
    params: [{ name: 'amount', type: 'number' }, { name: 'pair', type: 'string' }],
    requiresAuth: false,
    keywords: ['rates', 'exchange', 'fx', 'convert', 'usd to kes', 'kes to usd'],
    run: (ctx, { amount, pair }) => {
      if (ctx.selectStack) ctx.selectStack('exchange_rates');
      return amount && pair
        ? `Opening exchange rates for ${amount} ${pair}. Click Demo to see competing bank quotes.`
        : 'Opening exchange rates. Enter your currency pair and amount, then click Demo for quotes.';
    },
  },
  {
    name: 'compliance.start',
    description: 'Start compliance process',
    params: [],
    requiresAuth: false,
    keywords: ['compliance', 'kyc', 'onboarding', 'documents'],
    run: (ctx) => {
      if (ctx.selectStack) ctx.selectStack('compliance');
      return 'Opening compliance. Fill in your details to check pre-approval status across partner banks.';
    },
  },
  {
    name: 'hedging.quote',
    description: 'Get hedging quotes',
    params: [],
    requiresAuth: false,
    keywords: ['hedge', 'hedging', 'forwards', 'protect'],
    run: (ctx) => {
      if (ctx.selectStack) ctx.selectStack('hedging');
      return 'Opening hedging devices. Select your capital currency, hedge currency, and instrument to see available hedge rates.';
    },
  },
  {
    name: 'hedging.model',
    description: 'Run the 50/50 hedging model',
    params: [],
    requiresAuth: false,
    keywords: ['50/50', 'model', 'hedging model', 'break-even'],
    run: (ctx) => {
      if (ctx.selectStack) ctx.selectStack('hedging');
      return 'Opening hedging. Click Demo, then Select an instrument to see the 50/50 model with break-even analysis.';
    },
  },
  {
    name: 'liquidity.list',
    description: 'View secondary market listings',
    params: [],
    requiresAuth: false,
    keywords: ['liquidity', 'secondary', 'listings', 'market'],
    run: (ctx) => {
      if (ctx.selectStack) ctx.selectStack('liquidity');
      return 'Opening liquidity. Click View Market to see your portfolio and available secondary market listings.';
    },
  },
  {
    name: 'execution.run',
    description: 'Run execution simulation',
    params: [],
    requiresAuth: false,
    keywords: ['execute', 'execution', 'transaction', 'simulate'],
    run: (ctx) => {
      if (ctx.selectStack) ctx.selectStack('execution');
      return 'Opening transaction execution. Click Demo to simulate the full 8-step lifecycle.';
    },
  },
  {
    name: 'portfolio.open',
    description: 'Open portfolio view',
    params: [],
    requiresAuth: false,
    keywords: ['portfolio', 'holdings', 'performance', 'positions'],
    run: (ctx) => {
      if (ctx.selectStack) ctx.selectStack('portfolio');
      return 'Opening portfolio. Click Demo to see a sample portfolio with holdings, performance, and projections.';
    },
  },
  {
    name: 'navigate',
    description: 'Navigate to a page',
    params: [{ name: 'path', type: 'string', required: true }],
    requiresAuth: false,
    keywords: ['navigate to', 'go to page', 'take me to'],
    run: (ctx, { path }) => {
      if (ctx.navigate) ctx.navigate(path);
      return `Navigating to ${path}.`;
    },
  },
  {
    name: 'flow.status',
    description: 'Report flow progress',
    params: [],
    requiresAuth: false,
    keywords: ['progress', 'how far', 'steps completed', 'where am i', 'status'],
    run: (ctx) => {
      if (!ctx.getCompletedPillars) return 'Flow tracking is not active.';
      const completed = ctx.getCompletedPillars();
      if (completed.length === 0) return 'You haven\'t started the flow yet. Begin with Yield discovery by clicking Demo.';
      if (completed.length === 7) return 'You\'ve completed all 7 steps of the stack. Well done!';
      return `You've completed ${completed.length} of 7 steps: ${completed.map(k => k.replace(/_/g, ' ')).join(', ')}. Keep going!`;
    },
  },
  {
    name: 'flow.next',
    description: 'Navigate to next uncompleted panel',
    params: [],
    requiresAuth: false,
    keywords: ['next step', 'continue', 'proceed', 'what\'s next'],
    run: (ctx) => {
      if (!ctx.getNextPillar) return 'Flow tracking is not active.';
      const next = ctx.getNextPillar();
      if (!next) return 'All steps are complete! You\'ve gone through the entire stack.';
      if (ctx.selectStack) ctx.selectStack(next.key);
      return `Moving to ${next.label} (step ${next.n} of 7).`;
    },
  },
];

// ─── Banned words filter ───
const BANNED_WORDS = [
  'guaranteed', 'risk-free', 'no-risk', 'sure thing', 'certain returns',
  'promise', 'zero risk',
];

export function filterBannedWords(text) {
  let filtered = text;
  BANNED_WORDS.forEach(word => {
    const regex = new RegExp(word, 'gi');
    filtered = filtered.replace(regex, '[redacted]');
  });
  return filtered;
}

// ─── PII scrub ───
const PII_PATTERNS = [
  /\b\d{6,}\b/g,                    // ID numbers (6+ digits)
  /\b[A-Z]{2}\d{2}[A-Z0-9]{4,}\b/g, // IBAN-like
  /\b\+?\d{10,15}\b/g,              // Phone numbers
];

export function scrubPII(text) {
  let scrubbed = text;
  PII_PATTERNS.forEach(pattern => {
    scrubbed = scrubbed.replace(pattern, '[REDACTED]');
  });
  return scrubbed;
}

// ─── Intent matching ───
export function matchIntent(text) {
  const lower = text.toLowerCase().trim();

  for (const intent of INTENTS) {
    for (const kw of intent.keywords) {
      if (lower.includes(kw)) {
        return intent;
      }
    }
  }

  return null;
}

// ─── Extract parameters from text ───
export function extractParams(text) {
  const params = {};

  // Amount extraction
  const amountMatch = text.match(/(\d[\d,]*(?:\.\d+)?)\s*(usd|kes|chf|aed|gbp|jpy)/i);
  if (amountMatch) {
    params.amount = parseFloat(amountMatch[1].replace(/,/g, ''));
    params.currency = amountMatch[2].toUpperCase();
  }

  // Pair extraction
  const pairMatch = text.match(/(usd|kes|chf|aed|gbp|jpy)\s*(?:to|\/)\s*(usd|kes|chf|aed|gbp|jpy)/i);
  if (pairMatch) {
    params.pair = `${pairMatch[1].toUpperCase()}/${pairMatch[2].toUpperCase()}`;
  }

  // Stack element extraction
  const elementMap = {
    'yield': 'yield_discovery',
    'rates': 'exchange_rates',
    'exchange': 'exchange_rates',
    'compliance': 'compliance',
    'kyc': 'compliance',
    'hedging': 'hedging',
    'hedge': 'hedging',
    'liquidity': 'liquidity',
    'secondary': 'liquidity',
    'execution': 'execution',
    'transaction': 'execution',
    'portfolio': 'portfolio',
    'holdings': 'portfolio',
  };

  for (const [keyword, element] of Object.entries(elementMap)) {
    if (text.toLowerCase().includes(keyword)) {
      params.element = element;
      break;
    }
  }

  return params;
}

export { INTENTS, BANNED_WORDS };
export default INTENTS;
